import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { logError } from '../../../../../utils/logger';
import {
  ApproveRejectActions,
  ConfirmationModal,
  Loader,
  SectionTitle,
} from '../../../../shared/components';
import SuccessModal from '../../../../shared/components/feedback/SuccessModal';
import { formatDate } from '../../../../shared/utils/misc';
import { useOfferTiming } from '../../shared/hooks';
import { getLatestOffer } from '../../shared/utils';
import { getOfferContext, updateOfferAction } from '../interview.public.service';

export default function OfferAcceptRejectPage() {
  const { applicationId } = useParams();

  const [loading, setLoading] = useState(false);
  const [offerData, setOfferData] = useState(null);

  const fetchOfferContext = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    try {
      const data = await getOfferContext(applicationId);
      setOfferData(data);
    } catch (error) {
      toast.error(
        logError({
          error: error,
          message: 'Failed to fetch candidate details',
          code: 'CANDIDATE_DETAILS_FETCH_FAILED',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    fetchOfferContext();
  }, [fetchOfferContext]);

  const latestOffer = useMemo(() => getLatestOffer(offerData?.offers), [offerData]);
  return (
    <div className='column gap-5'>
      <SectionTitle title='ONBOARDING PROCESS - OFFER LETTER' />
      {loading ? (
        <Loader />
      ) : latestOffer ? (
        <OfferLetterItem offer={latestOffer} refresh={fetchOfferContext} />
      ) : (
        <Alert severity='info'>
          No offer letter is available for this candidate at the moment.
        </Alert>
      )}
    </div>
  );
}

function OfferLetterItem({ offer, refresh }) {
  const { offerLetter } = offer || {};
  const timing = useOfferTiming(offerLetter?.sendDate);

  const [actionMode, setActionMode] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { applicationId } = useParams();

  const updateOffer = async () => {
    if (!applicationId || !actionMode) return;
    const action = actionMode === 'accept' ? 'accepted' : 'rejected';
    setActionMode(null);
    try {
      await updateOfferAction(applicationId, { resource: 'offer_letter', action: actionMode });
      setSuccessMessage(
        `Offer letter ${action} successfully. We will be in touch with further details.`,
      );
      refresh();
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(
        logError({
          error,
          message: 'Failed to update offer letter',
          code: 'OFFER_LETTER_UPDATE_FAILED',
        }),
      );
    }
  };

  const isActionableOffer = offerLetter?.status === 'sent' && !timing?.isExpired;

  return (
    <>
      <div className='column gap-3'>
        <div className='grid grid--3-cols grid-start'>
          <span>Offer letter</span>

          <div className='column gap-1 text--secondary'>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
              <div className='row'>
                <Icon icon='tabler:check' width={18} />
                <span>Sent to candidate</span>
              </div>
              <span>{formatDate(offerLetter?.sendDate)}</span>
            </Box>

            {offerLetter?.status === 'accepted' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
                <div className='row'>
                  <Icon icon='tabler:check' width={18} />
                  <span>Accepted by candidate</span>
                </div>
                <span>{formatDate(offerLetter?.acceptedDate)}</span>
              </Box>
            )}

            {offerLetter?.status === 'rejected' && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr',
                  '&  svg': { ml: -0.5 },
                }}
              >
                <div className='row'>
                  <Icon icon='basil:cross-outline' width={28} />
                  <span>Rejected by candidate</span>
                </div>
                <span>{formatDate(offerLetter?.rejectedDate)}</span>
              </Box>
            )}

            {offerLetter?.status === 'sent' && timing && !timing.isExpired && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
                <div className='row'>
                  <Icon icon='mdi:account-pending-outline' width={18} />
                  <span>Approval pending from candidate</span>
                </div>
                <span>Time left: {timing.hoursLeft} hrs</span>
              </Box>
            )}

            {offerLetter?.status === 'sent' && timing?.isExpired && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr' }}>
                <div className='row'>
                  <Icon icon='zondicons:exclamation-outline' width={18} />
                  <span>Action window has expired</span>
                </div>
              </Box>
            )}
          </div>

          {offerLetter?.url && (
            <MuiLink
              href={offerLetter.url}
              target='_blank'
              rel='noopener noreferrer'
              sx={{ color: 'grey.500', justifySelf: 'end', padding: 0 }}
            >
              View offer letter
            </MuiLink>
          )}
        </div>
        {isActionableOffer && (
          <ApproveRejectActions
            approveLabel='Accept'
            onApprove={() => setActionMode('accept')}
            onReject={() => setActionMode('reject')}
          />
        )}
      </div>
      <ConfirmationModal
        open={actionMode === 'accept'}
        onClose={() => setActionMode(null)}
        title='Accept offer letter'
        message='Are you sure you want to accept this offer letter?'
        confirmText='Accept'
        icon='icon-park-solid:success'
        onConfirm={updateOffer}
        sx={{ '& > svg': { color: 'primary.light' } }}
      />

      <ConfirmationModal
        open={actionMode === 'reject'}
        onClose={() => setActionMode(null)}
        title='Reject offer letter'
        message='Are you sure you want to reject this offer letter?'
        confirmText='Reject'
        icon='icons8:cancel'
        onConfirm={updateOffer}
      />

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage} maxWidth="sm"
      />
    </>
  );
}
